import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import PaiHelpIcon from 'features/ui/base-components/pai-help-icon';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  open: boolean;
  loading: boolean;

  numberOfContactsToLoad: number;

  handleNumberOfContactsToLoadChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleClose(): void;

  handleContinueSearch(): void;
}

const LoadContactsNumberDialog: React.FC<Props> = ({
  open,
  loading,
  numberOfContactsToLoad,
  handleNumberOfContactsToLoadChange,
  handleClose,
  handleContinueSearch,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Dialog open={open} classes={{ paper: classes.paper }} fullWidth={true} maxWidth={'md'}>
      <DialogTitle className={classes.title}>
        <Typography className={classes.titleText}>{t('addMoreCandidates')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={2}>
          <Stack gap={1}>
            <Typography className={classes.boldText}>{t('addCandidatesToPreQualificationList')}</Typography>
            <Typography className={classes.greyText}>
              <Trans i18nKey={'linkedinLimitsExplanation'}>
                Due to LinkedIn’s limitations and security policies, we can analyze approximately 400 LinkedIn profiles per day across all campaigns,
                with a maximum of 150 profiles per hour.{' '}
                <span className={classes.highlight}>
                  While you can load more profiles, exceeding these limits increases the risk of your LinkedIn account being flagged or blocked.
                </span>
              </Trans>
            </Typography>
            <Typography className={classes.greyText}>{t('leadLoadingAdvice')}</Typography>
          </Stack>
          <Divider />
          <Stack gap={2}>
            <Stack gap={1}>
              <Typography className={classes.boldText}>{t('setTotalContactsTitle')}</Typography>
              <Typography className={classes.greyText}>{t('setTotalContactsDescription')}</Typography>
            </Stack>
            <Stack gap={1}>
              <PaiTextField
                value={numberOfContactsToLoad}
                variant={'background'}
                type={'number'}
                onChange={handleNumberOfContactsToLoadChange}
                style={{ maxWidth: '350px' }}
              />
              {numberOfContactsToLoad > 150 ? (
                <Stack direction={'row'} alignItems={'center'} gap={1}>
                  <PaiHelpIcon tooltipText={false} variant={'warning'} />
                  <Typography className={classes.warningText}>{t('contactsLoadingLimitWarning', { count: 150 })}</Typography>
                </Stack>
              ) : null}
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink text={t('cancel')} size={'sm'} underline={'always'} action={handleClose} />
        <PaiButton
          text={t('startLoadingContacts')}
          size={'small'}
          border={'rounded'}
          action={handleContinueSearch}
          disabled={!numberOfContactsToLoad}
          loading={loading}
        />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '24px !important',
    whiteSpace: 'pre-wrap',
  },
  boldText: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 'bold',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
  highlight: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  warningText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.warning.main,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 24px 20px 24px',
  },
}));

export default LoadContactsNumberDialog;
